import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import Footer from 'src/components/layouts/Footer'
import { data } from 'src/components/pages/Home/consts'
import { useAnalytics } from 'src/common/analytics'
import {
  getLoginEmailError,
  isValidLoginEmail,
} from 'src/common/utils/loginEmail'
import Img from 'src/components/shared/Img'
import LoginTrustStrip from './LoginTrustStrip'
import Upscope from './Upscope'
import { APPLY_FDR_URL } from '../../../common/constants'

const OVERLAY_FOOTER_DESKTOP_MQ = '(min-width: 1280px)'

const loginComp = {
  title: 'Client Dashboard Login',
  sub: 'You can use your Freedom Debt Relief account to log into your dashboard.',
  emailLabel: 'Enter your email',
  continue: 'Continue',
  notClientLine:
    'Not a client? Your first step toward a debt-free future starts here.',
  explore: 'Explore your options',
}

const ClientDashboardLoginOverlay = ({
  onClose,
  footerProps,
  openedFromShowFormCookie = false,
}) => {
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [footerInContentShell, setFooterInContentShell] = useState(false)
  const { track } = useAnalytics()
  const emailInputRef = useRef(null)
  const hasFiredInputClick = useRef(false)

  useLayoutEffect(() => {
    const mq = window.matchMedia(OVERLAY_FOOTER_DESKTOP_MQ)
    const sync = () => setFooterInContentShell(mq.matches)
    sync()
    mq.addEventListener('change', sync)

    const frameId = window.requestAnimationFrame(() => {
      emailInputRef.current?.focus({ preventScroll: true })
    })

    return () => {
      mq.removeEventListener('change', sync)
      window.cancelAnimationFrame(frameId)
    }
  }, [])

  useEffect(() => {
    document.body.classList.add('overflow-hidden')

    return () => document.body.classList.remove('overflow-hidden')
  }, [])

  useEffect(() => {
    // Login overlay view — fires whenever the overlay is displayed
    track(
      {
        element_name: 'login-overlay',
        nav_link_section: 'Login Overlay',
      },
      {},
      'login_view'
    )
  }, [track])

  const errorMessage = getLoginEmailError(email, { touched })
  const showError = Boolean(errorMessage)
  const hasInput = email.trim().length > 0

  const pageContentShell = classNames(
    'mx-auto w-full max-w-360 min-w-0',
    'px-4 md:px-5 xl:px-20',
    footerProps.footerClassName
  )

  const overlayFooterClassName = classNames(
    footerProps.footerClassName,
    'px-4! md:px-5! xl:px-20!'
  )

  const handleExploreOptionsClick = useCallback(
    (e) => {
      track(
        {
          click_text: 'Explore your options',
          click_type: 'Link Click',
          flow_name: 'Signin',
          flow_step_name: 'Signin | Login: Explore your options Clicked',
          track_event: 'Signin Explore',
        },
        {},
        'event'
      )
      e.preventDefault()
      if (footerProps.isHome) {
        onClose()

        return
      }

      window.location.href = APPLY_FDR_URL()
    },
    [footerProps.isHome, onClose, track]
  )

  const trackInputClick = useCallback(() => {
    track(
      {
        flow_event: 'Lead CTA Click',
        flow_name: 'Signin',
        flow_step_name: 'Signin | Login: Input Click',
        nav_link_section: 'Form',
        track_event: 'form_view',
      },
      {},
      'event'
    )
  }, [track])

  const handleEmailFocus = useCallback(() => {
    if (hasFiredInputClick.current) return
    hasFiredInputClick.current = true
    trackInputClick()
  }, [trackInputClick])

  const handleEmailClick = useCallback(() => {
    trackInputClick()
  }, [trackInputClick])

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value)
      if (showError) setTouched(false)
    },
    [showError]
  )

  const handleContinue = useCallback(
    async (e) => {
      e.preventDefault()
      setTouched(true)
      if (!isValidLoginEmail(email)) return
      const hashBuffer = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(email)
      )
      const form_email_hash = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
      track(
        {
          click_text: 'Continue',
          flow_event: 'Lead CTA Click',
          flow_name: 'Signin',
          flow_step_name: 'Signin | Login: Button Click',
          form_email: email,
          form_email_hash,
          nav_link_section: 'Form',
          track_event: 'form_view',
        },
        {},
        'event'
      )
      const currentParams = new URLSearchParams(window.location.search)
      currentParams.set('email', email)
      window.location.href = `/login?${currentParams.toString()}`
    },
    [email, track]
  )

  const overlayFooter = (
    <Footer
      {...footerProps}
      footerClassName={overlayFooterClassName}
      hideClientDashboardLink
    />
  )

  return (
    <div
      className="z-220 fixed inset-0 flex animate-clientDashboardLoginOverlay flex-col overflow-y-auto bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-dashboard-login-title"
      data-testid="client-dashboard-login-overlay"
    >
      <Upscope />
      <div className={classNames('shrink-0 bg-white', pageContentShell)}>
        <div
          className={classNames(
            'mx-auto flex w-full max-w-[327px] justify-center py-2'
          )}
        >
          <a
            href="/"
            className="inline-flex h-12 shrink-0 items-center"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
          >
            <Img
              src="/next-assets/icons/fdr-logo.svg"
              alt="Freedom Debt Relief"
              width={166}
              height={48}
              layout="intrinsic"
              className="max-w-50 h-12"
              imgClassName="h-12 w-auto"
            />
          </a>
        </div>
      </div>
      <div
        className={classNames('shrink-0 bg-white', pageContentShell)}
        aria-hidden
      >
        <div className="h-px w-full min-w-0 bg-[#E8E8E8]" />
      </div>
      {openedFromShowFormCookie ? null : (
        <div className={classNames('shrink-0 bg-white', pageContentShell)}>
          <div
            className={classNames(
              'mx-auto w-full max-w-[327px]',
              'flex justify-end pt-6 pb-0'
            )}
          >
            <button
              type="button"
              className="size-6 flex shrink-0 items-center justify-center opacity-90 hover:opacity-100"
              aria-label="Close"
              onClick={onClose}
              data-testid="client-dashboard-login-close"
            >
              <Img
                src="/next-assets/icons/close.svg"
                alt=""
                width={24}
                height={24}
                layout="intrinsic"
                className="size-6"
                imgClassName="size-6"
              />
            </button>
          </div>
        </div>
      )}

      <div className="flex w-full min-w-0 shrink-0 flex-col bg-white font-ultramarine">
        <div
          className={classNames(
            pageContentShell,
            'min-w-0 pb-12',
            openedFromShowFormCookie ? 'pt-10 md:pt-12' : 'pt-4'
          )}
        >
          <div
            className={classNames(
              'mx-auto w-full min-w-0 xl:max-w-screen-lg',
              'min-w-0'
            )}
          >
            <form
              className="flex flex-col items-center gap-6"
              onSubmit={handleContinue}
              noValidate
            >
              <div className="flex w-full max-w-[295px] flex-col gap-2 text-center">
                <h2
                  id="client-dashboard-login-title"
                  className="leading-1.3 text-2xl font-bold text-blue-585"
                >
                  {loginComp.title}
                </h2>
                <p
                  className="text-sm font-normal leading-[19.6px] text-content-primary"
                  data-testid="sign-in-subtitle"
                >
                  {loginComp.sub}
                </p>
              </div>

              <div className="flex w-full max-w-[303px] flex-col gap-8">
                <div className="flex w-full min-w-0 shrink-0 flex-col">
                  <label
                    htmlFor="client-dashboard-login-email"
                    className={classNames(
                      'leading-1.4 z-2 pointer-events-none relative mb-[-9px] ml-[11px] w-fit max-w-[calc(100%-22px)] bg-white px-1 text-12 font-normal',
                      showError ? 'text-red-fdr' : 'text-blue-585'
                    )}
                  >
                    {loginComp.emailLabel}
                  </label>
                  <div
                    className={classNames(
                      'z-1 relative flex min-h-14 w-full items-center rounded-8 border border-solid bg-white px-4 shadow-none transition-colors',
                      showError
                        ? 'border-red-850 focus-within:border-red-850'
                        : 'border-blue-585 focus-within:border-blue-585'
                    )}
                    onMouseDown={(e) => {
                      if (e.target !== emailInputRef.current) {
                        e.preventDefault()
                        emailInputRef.current?.focus({ preventScroll: true })
                      }
                    }}
                  >
                    <input
                      id="client-dashboard-login-email"
                      ref={emailInputRef}
                      data-testid="email-form-sign-in"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onFocus={handleEmailFocus}
                      onClick={handleEmailClick}
                      onChange={handleEmailChange}
                      className="leading-1.4 min-h-0 w-full min-w-0 flex-1 border-0 bg-transparent py-3 font-ultramarine text-body-medium font-normal text-content-primary outline-none ring-0 focus:ring-0"
                      aria-invalid={showError}
                      aria-describedby={
                        showError
                          ? 'client-dashboard-login-email-error'
                          : undefined
                      }
                    />
                  </div>
                  {showError ? (
                    <p
                      id="client-dashboard-login-email-error"
                      role="alert"
                      className="leading-1.4 mt-2 text-sm font-normal text-red-fdr"
                    >
                      {errorMessage}
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  data-testid="email-form-continue-button"
                  disabled={!hasInput}
                  className={classNames(
                    'leading-1.2 flex min-h-[37.6px] w-full items-center justify-center rounded-lg px-6 py-2 text-heading-4 font-bold tracking-[0.36px] transition-colors',
                    hasInput
                      ? 'bg-blue-585 text-[#F9F9F9] hover:bg-blue-600'
                      : 'cursor-not-allowed bg-[#F3F3F3] text-gray-230'
                  )}
                >
                  {loginComp.continue}
                </button>
              </div>
            </form>

            <div className="mt-8 flex flex-col items-center gap-8">
              <div className="w-full max-w-[327px] rounded-[10px] border border-solid border-gray-230 bg-white px-3 py-5">
                <div className="flex gap-4">
                  <div className="size-10 flex shrink-0 items-center justify-center">
                    <Img
                      src="/next-assets/icons/client-dashboard-login/not-client-magnify-dollar.svg"
                      alt=""
                      width={35}
                      height={40}
                      layout="intrinsic"
                      className="h-10 w-[35px]"
                      imgClassName="h-10 max-h-10 w-[35px] object-contain object-left"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <p className="leading-1.4 text-sm font-normal text-content-primary">
                      {loginComp.notClientLine}
                    </p>
                    <button
                      type="button"
                      className="leading-1.2 inline-flex w-fit items-center gap-1 py-2 text-sm font-bold tracking-[0.28px] text-blue-585"
                      onClick={handleExploreOptionsClick}
                    >
                      {loginComp.explore}
                      <Img
                        src="/next-assets/icons/client-dashboard-login/explore-arrow.svg"
                        alt=""
                        width={16}
                        height={16}
                        layout="intrinsic"
                        className="size-4 shrink-0"
                        imgClassName="size-4"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 w-full min-w-0">
            <LoginTrustStrip iconStrip={data.heroBanner.iconStrip} />
          </div>
          {footerInContentShell ? overlayFooter : null}
        </div>

        {footerInContentShell ? null : overlayFooter}
      </div>
    </div>
  )
}

export default ClientDashboardLoginOverlay
